import useDebounceFn from '@hooks/useDebounceFn';
import { useEffect, useRef, useCallback } from 'react';
/**
 * useInfiniteScroll
 *
 * Triggers onFetchMore when the user scrolls to the top of the container (for upward infinite scroll).
 *
 * @param containerRef - React ref to the scrollable container
 * @param onFetchMore - Callback to fetch more items
 * @param hasMore - Whether there are more items to fetch
 * @param isLoading - Whether a fetch is currently in progress
 * @param threshold - How close (in px) to the top before triggering (default: 100)
 */
function useInfiniteScroll(containerRef, onFetchMore, hasMore, isLoading, threshold = 100) {
    const prevScrollHeight = useRef(null);
    const fetchingRef = useRef(false);
    const handleScrollFn = useCallback(() => {
        const container = containerRef.current;
        if (!container || !hasMore || isLoading || fetchingRef.current)
            return;
        if (container.scrollTop <= threshold) {
            prevScrollHeight.current = container.scrollHeight;
            fetchingRef.current = true;
            onFetchMore();
        }
    }, [containerRef, onFetchMore, hasMore, isLoading, threshold]);
    const handleScroll = useDebounceFn(handleScrollFn, 100);
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        container.addEventListener('scroll', handleScroll);
        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [containerRef, handleScroll]);
    // Reset fetchingRef when loading is done
    useEffect(() => {
        if (!isLoading) {
            fetchingRef.current = false;
        }
    }, [isLoading]);
    // Optional: Maintain scroll position after fetch
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        if (prevScrollHeight.current !== null && isLoading === false) {
            // After loading, restore scroll position so content doesn't jump
            const diff = container.scrollHeight - (prevScrollHeight.current || 0);
            container.scrollTop += diff;
            prevScrollHeight.current = null;
        }
    }, [isLoading, containerRef]);
}
export default useInfiniteScroll;
